import React, { useState } from 'react';
import { Field, Form } from 'formik';

// Material UI
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import AddPhotoAlternateIcon from '@material-ui/icons/AddPhotoAlternate';
import CircularProgress from '@material-ui/core/CircularProgress';

// Data
import { getMessages } from './update-company-form.messages';
import { UPDATE_COMPANY_FORM_ID } from './update-company-form.constants';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { TIMEZONE_NAME_OPTIONS } from '../../../lib/timezone.constants';
import logo from '../../../assets/logo.png';

// Components
import { OutlineButton, PrimaryButton } from '../../_core/_ui/buttons.component';
import { FormikInput } from '../../_core/Formik/formik-input.component';
import { FormikSelect } from '../../_core/Formik/formik-select.component';
import { FocusFirstInvalidField } from '../../_core/Formik/focus-first-invalid-field.component';
import { DropFile } from '../../_core/DropFile/drop-file.component';
import { FieldCaption } from '../../_core/_ui/field-caption.component';
import { SettingsSection } from '../../_core/_ui/settings-section.component';
import { FormHelperText } from '../../_core/_ui/forms.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { FormikSettingCheckbox } from '../../_core/Formik/formik-setting-checkbox.component';
import { RetainJerseyAssignmentsField } from '../../JerseyAssignment/retain-jersey-assignments-field.component';
import { getMessages as getAttendanceMessages } from '../../Attendance/attendance.messages';
import { getMessages as getReportCardAccessMessages } from '../../ReportCardAccess/report-card-access.messages';
import { CalibrationSettingsField } from './calibration-settings-field.component';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';

interface IUpdateCompanyFormProps {
  company: ICompany;
  user: IUser | null;
  form: any;
  setCompanyLogo: Function;
  submitError?: string | null;
  applyAttendanceDefaultToAssessments: () => Promise<any>;
  canEditAttendanceDefault: boolean;
  canEditReportCardDefault: boolean;
}

export const UpdateCompanyForm: React.FC<IUpdateCompanyFormProps> = ({ company, user, form, setCompanyLogo, submitError, applyAttendanceDefaultToAssessments, canEditAttendanceDefault, canEditReportCardDefault }) => {
  const [logoPhase, setLogoPhase] = useState<'idle' | 'picking' | 'uploading'>('idle');
  const [logoError, setLogoError] = useState<React.ReactNode>(null);
  const [isApplyingDefault, setApplyingDefault] = useState(false);
  const [applyResult, setApplyResult] = useState<React.ReactNode>(null);

  const termLookup = useCompanySportTerminology()
  const messages = getMessages(termLookup);
  // The RSVP copy lives in attendance.messages, so every scope that renders it
  // (session, assessment, association) stays worded in step.
  const attendanceMessages = getAttendanceMessages(termLookup);
  const reportCardAccessMessages = getReportCardAccessMessages(termLookup);

  const { isSubmitting } = form;

  // See "Upload buttons go busy at file-dialog open, not upload start" in CLAUDE.md.
  const isLogoBusy = logoPhase !== 'idle';

  const onReceivedFiles = (companyId: string, files: FileList) => {
    setLogoPhase('uploading');
    setLogoError(null);
    setCompanyLogo(companyId, files, user?.token)
      .then(() => setLogoPhase('idle'))
      .catch((err: Error) => {
        console.error('setCompanyLogo failed:', err);
        setLogoPhase('idle');
        setLogoError(<IntlMsg {...messages.errUploadLogo} />);
      });
  }

  const handleApplyAttendanceDefault = () => {
    setApplyingDefault(true);
    setApplyResult(null);

    applyAttendanceDefaultToAssessments()
      .then((res) => {
        const count = res?.data?.applyIsAttendanceRequestedDefaultToAssessments?.updatedCount ?? 0;
        setApplyResult(
          <IntlMsg
            {...messages.msgAppliedToAssessments}
            values={{ count, assessments: pluralize(count, 'assessment') }}
          />
        );
      })
      .catch((error) => {
        console.error('applyAttendanceDefaultToAssessments failed:', error);
        setApplyResult(<IntlMsg {...messages.errApplyDefaultFailed} />);
      })
      .finally(() => setApplyingDefault(false));
  }

  return (
    <Form id={UPDATE_COMPANY_FORM_ID}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Field fid="ucf" name="name" component={FormikInput} label={messages.lblCompanyName} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Box mt={1}>
            <Field fid="ucf" name="timezone" label={messages.lblTimezone} component={FormikSelect} options={TIMEZONE_NAME_OPTIONS} />
          </Box>
        </Grid>
      </Grid>
      <SettingsSection title={<IntlMsg {...messages.titleCommunicationBranding} />}>
        <Box display="flex">
          <DropFile
            disabled={isLogoBusy}
            onFileDialogOpen={() => setLogoPhase('picking')}
            onFileDialogCancel={() => setLogoPhase('idle')}
            onReceivedFiles={(files: FileList) => onReceivedFiles(company._id, files)}
          >
            <Button style={{ backgroundColor: 'white' }} disabled={isLogoBusy}>
              <IntlMsg {...(logoPhase === 'uploading' ? messages.btnUploading : messages.btnChangeLogo)} />
              {' '}
              {isLogoBusy ? <CircularProgress size={20} /> : <AddPhotoAlternateIcon />}
            </Button>
          </DropFile>
          <Box flexGrow={1} />
          <img src={company.logo ? company.logo : logo} alt="Missing Logo" style={{ marginRight: '32px', width: '200px' }} />
        </Box>
        {logoError && <FormHelperText error>{logoError}</FormHelperText>}
      </SettingsSection>

      <Field fid="ucf" name="companyWebsite" label={messages.lblCompanyWebsite} component={FormikInput} />
      <Field fid="ucf" name="communicationEmail" component={FormikInput} label={messages.lblCommunicationEmail} />

      {canEditAttendanceDefault && (
        <SettingsSection title={<IntlMsg {...messages.titleAttendance} />}>
          <FormikSettingCheckbox
            fid="ucf"
            name="isAttendanceRequestedDefault"
            label={attendanceMessages.lblRequestRsvpsDefault}
            caption={<IntlMsg {...attendanceMessages.captionRequestRsvpsAssociationDefault} />}
          />
          <Box mt={3}>
            <OutlineButton
              type="button"
              disabled={isApplyingDefault || form.dirty}
              onClick={handleApplyAttendanceDefault}
            >
              <IntlMsg {...(isApplyingDefault ? messages.btnApplying : messages.btnApplyDefault)} />
            </OutlineButton>
            <FieldCaption>
              {form.dirty ? (
                <IntlMsg {...messages.helpSaveFirstToApply} />
              ) : (
                <>
                  <IntlMsg {...messages.helpApplyScopeApplies} />
                  <br />
                  <IntlMsg {...messages.helpApplyScopeUnchanged} />
                </>
              )}
            </FieldCaption>
            {applyResult && <FieldCaption>{applyResult}</FieldCaption>}
          </Box>
        </SettingsSection>
      )}

      {canEditAttendanceDefault && (
        <SettingsSection title={<IntlMsg {...messages.titleJerseys} />}>
          <RetainJerseyAssignmentsField scope="association" fid="ucf" />
        </SettingsSection>
      )}

      {canEditReportCardDefault && (
        <SettingsSection title={<IntlMsg {...messages.titleReportCards} />}>
          <FormikSettingCheckbox
            fid="ucf"
            name="reportCardsIncludeNonConcludedDefault"
            label={reportCardAccessMessages.lblIncludeNonConcludedDefault}
            caption={<IntlMsg {...reportCardAccessMessages.captionIncludeNonConcludedAssociationDefault} />}
          />
        </SettingsSection>
      )}

      <SettingsSection
        title={<IntlMsg {...messages.titleCalibration} />}
        description={<IntlMsg {...messages.descCalibration} />}
      >
        <CalibrationSettingsField fid="ucf" />
      </SettingsSection>

      <FormControl margin="normal">
        <PrimaryButton disabled={isSubmitting} type="submit">
          <IntlMsg {...(isSubmitting ? messages.btnSaving : messages.btnSaveSettings)} />
        </PrimaryButton>
      </FormControl>
      {submitError && (
        <Box mb={2}>
          <FormHelperText error>{submitError}</FormHelperText>
        </Box>
      )}

      <FocusFirstInvalidField formId={UPDATE_COMPANY_FORM_ID} />
    </Form>
  );
};
